/**
 * @description A mapping of opacity percentages to their corresponding hexadecimal alpha values.
 * These hex alpha values can be appended to the end of 6-digit hex color codes to include
 * opacity in the color representation. This mapping is useful for converting a
 * percentage-based opacity into a hexadecimal alpha value for use in CSS or other
 * styling contexts.
 */
const opacityPercentages = {
  100: 'FF',
  95: 'F2',
  90: 'E6',
  85: 'D9',
  80: 'CC',
  75: 'BF',
  70: 'B3',
  65: 'A6',
  60: '99',
  55: '8C',
  50: '80',
  45: '73',
  40: '66',
  35: '59',
  30: '4D',
  25: '40',
  20: '33',
  15: '26',
  10: '1A',
  5: '0D',
  0: '00',
};

/**
 * @description Converts a percentage opacity value to its corresponding alpha hex value
 * and appends it to a given base color. If no base color is provided, it
 * defaults to black (`#000000`).
 *
 * @param {Object} params - The parameters for the function.
 * @param {string} params.baseColor - The base color in hex format (e.g., `#RRGGBB`).
 * @param {number} [params.percentOpacity=100] - The percentage opacity (0 to 100).
 * @returns {string} The resulting color in hex format with the alpha channel appended.
 *
 * @example
 * const colorWithOpacity = percentOpacityToAlphaHex({
 *   baseColor: '#FF5733',
 *   percentOpacity: 50,
 * });
 * // `colorWithOpacity` will be '#FF573380'
 */
export function percentOpacityToAlphaHex({
  baseColor = '#000000',
  percentOpacity = 100,
}) {
  const alphaHex =
    opacityPercentages[percentOpacity] || opacityPercentages[100];

  return `${baseColor}${alphaHex}`;
}

/**
 * Generates an alpha palette by applying various opacity percentages to a base color.
 *
 * @param {Object} params - The options for generating the alpha palette.
 * @param {string} [params.colorPrefix='default'] - The prefix name for the generated color keys.
 * @param {string} [params.paletteColor='#000000'] - The base color in hexadecimal format.
 * @param {Array} [params.opacityPercentagesToGenerate=[]] - The opacity percentages to generate for the palette.
 * Defaulted to [] which will generate the entire alpha palette.
 * @returns {Object} An object where each key is a combination of the color name and opacity percentage,
 *                   and the value is the corresponding color with applied opacity in hexadecimal format.
 *
 * @example
 * const alphaPalette = generateAlphaPalette({
 *   colorPrefix: 'primary500',
 *   paletteColor: '#FF5733',
 * });
 * // `alphaPalette` will be:
 * // {
 * //   primary500_100: '#FF5733FF',
 * //   primary500_95: '#FF5733F2',
 * //   primary500_90: '#FF5733E6',
 * //   ...
 * // }
 *
 * @example
 * const alphaPalette = generateAlphaPalette({
 *   colorPrefix: 'primary500',
 *   paletteColor: '#FF5733',
 *   opacityPercentagesToGenerate = [35],
 * });
 * // `alphaPalette` will be:
 * // {
 * //   primary500_35: '#FF573359',
 * //   ...
 * // }
 */
export function generateAlphaPalette({
  colorPrefix = 'default',
  paletteColor = '#000000',
  opacityPercentagesToGenerate: opacityPercentagesFromArgs = [],
}) {
  const result = {};

  // If no opacity percentages are provided via args, generate all opacity percentages
  const opacityPercentagesToGenerate =
    opacityPercentagesFromArgs.length > 0
      ? opacityPercentagesFromArgs
      : Object.keys(opacityPercentages);

  for (const percent of opacityPercentagesToGenerate) {
    const colorWithOpacity = percentOpacityToAlphaHex({
      baseColor: paletteColor,
      percentOpacity: Number(percent),
    });
    result[`${colorPrefix}_${percent}`] = colorWithOpacity;
  }

  return result;
}
